/** Shown when a note, task, or contact date is missing or unreadable. */
export const MISSING_DATE_LABEL = "Zonder datum";

export type DateLike = string | number | Date | null | undefined;

/**
 * Parse a value for display or ordering.
 * Null, blank, and unparseable values stay missing — this never substitutes now.
 */
export function toValidDate(value: DateLike): Date | null {
  if (value == null) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Missing and invalid dates sort last, in either direction. Does not rewrite the value. */
export function compareByDate(
  a: DateLike,
  b: DateLike,
  order: "ASC" | "DESC" = "DESC",
): number {
  const at = toValidDate(a)?.getTime();
  const bt = toValidDate(b)?.getTime();
  if (at == null && bt == null) return 0;
  if (at == null) return 1;
  if (bt == null) return -1;
  return order === "ASC" ? at - bt : bt - at;
}

/**
 * Date to store on a new note.
 * Empty and invalid stay null. A real value becomes an ISO timestamp.
 * This never substitutes now.
 */
export function noteDateForCreate(value: DateLike): string | null {
  return toValidDate(value)?.toISOString() ?? null;
}

/** Timestamp to store. Empty and invalid stay null; a real value is kept as-is. */
export function noteDateToPersist(value: DateLike): string | null {
  if (typeof value === "string" && toValidDate(value)) return value;
  const date = toValidDate(value);
  return date ? date.toISOString() : null;
}

/**
 * Date to write on an existing note.
 * Undefined means omit the field: an untouched save leaves the stored value
 * as-is, including an invalid string. A change writes the submitted value
 * (empty or unreadable becomes null).
 */
export function persistedNoteDate(
  submitted: DateLike,
  dateFieldDirty: boolean,
): string | null | undefined {
  if (!dateFieldDirty) return undefined;
  return noteDateToPersist(submitted);
}
